import type { PostHogFlagsResult } from '@/lib/posthogServerFlags';
import * as Sentry from '@sentry/nextjs';
import { Builder } from '@builder.io/react';
import posthogClient from '@/lib/posthogWrapper';
import { GEO_STATE_COOKIE } from '@/constants/usStates';
import { getCookie, readSplitUrlExposureArms } from '@/lib/cookies';
import { isFlagTesterActive, isQaRequest } from '@/utils/flagTester';
import { getServedPathname } from '@/utils/servedPage';
import { normalizePath } from '@/utils/splitUrlRedirect/splitUrlRedirect';

type Arm = PostHogFlagsResult['flags'][string];

/** `flagKey:arm` pairs already recorded in this page load. */
const recordedExposures = new Set<string>();

export function resetRecordedExposuresForTests(): void {
	recordedExposures.clear();
}

/** The edge served a page other than the one in the address bar. */
function wasRewritten(): boolean {
	const servedPathname = getServedPathname();
	return (
		servedPathname !== null &&
		// `/sq/` and `/sq` are the same page: trailing-slash URLs are served without a redirect.
		normalizePath(servedPathname) !== normalizePath(window.location.pathname)
	);
}

/**
 * geo_state is set only by the middleware, on every response. Its presence means cookies the server
 * sets reach this browser. posthog-js's own cookie cannot tell this, because the browser writes it.
 */
function serverCookiesArrive(): boolean {
	return getCookie(GEO_STATE_COOKIE) !== null;
}

/** A rewrite proves the edge chose an arm; without the cookie that arm is lost to the experiment. */
function reportIfDecisionLost(): void {
	// Recorded earlier in this load: a revisit served from Next's cache after the cookie expired.
	if (recordedExposures.size > 0) return;
	if (!wasRewritten() || !serverCookiesArrive()) return;

	Sentry.withScope((scope) => {
		scope.setTag('api', 'PostHog');
		scope.setTag('error_type', 'lost_decision');
		scope.setTag('feature', 'split-url-redirect');
		scope.setTag('module', 'splitUrlExposure');
		scope.setLevel('warning');
		scope.setContext('served_page', {
			servedPath: getServedPathname(),
			browserPath: window.location.pathname,
		});
		Sentry.captureMessage('Split-url variant served without an exposure cookie');
	});
}

/** Visits whose arm was forced or skipped at the edge; they stay out of the experiment. */
function isExcludedVisit(): boolean {
	return (
		// Read directly: useIsPreviewing() is still false on the first render.
		Builder.isEditing ||
		Builder.isPreviewing ||
		!!window.__E2E_FLAGS__ ||
		isQaRequest(new URLSearchParams(window.location.search)) ||
		isFlagTesterActive()
	);
}

/** posthog-js dedupes only its own getFeatureFlag() events, so this is the only dedupe. */
function recordExposureOnce(flagKey: string, arm: Arm): void {
	const exposureKey = `${flagKey}:${String(arm)}`;
	if (recordedExposures.has(exposureKey)) return;
	recordedExposures.add(exposureKey);

	posthogClient.capture('$feature_flag_called', {
		$feature_flag: flagKey,
		$feature_flag_response: arm,
	});
}

/**
 * Records the experiment exposure for each split-url test whose page the edge served. `_app.tsx`
 * calls this at module load, before hydration — the same moment a microfrontends child records — so both arms
 * of a test count visitors who leave before React loads. The arms are read from the exposure
 * cookie, the edge's decision for this request, not from posthog-js's store, which is not
 * refreshed on a soft navigation.
 */
export function recordSplitUrlExposures(): void {
	if (isExcludedVisit()) return;

	const servedArms = readSplitUrlExposureArms();
	if (!servedArms) {
		reportIfDecisionLost();
		return;
	}

	for (const [flagKey, arm] of Object.entries(servedArms)) {
		recordExposureOnce(flagKey, arm);
	}
}
